'use client';

import React from 'react';

import CartCheckoutCoupons from '@/components/cartCheckout/cartCheckoutPromoCodes';
import CartCheckoutTemporaryAuthorization from '@/components/cartCheckout/cartCheckoutTemporaryAuthorization';
import CartCheckoutTotals from '@/components/cartCheckout/cartCheckoutTotals';
import CartItems from '@/components/cart/cartItems';
import StylizedButton from '@/components/stylizedButton';

import { orderHasShippingEstimate } from '@/lib/hooks/usePartyCheckoutOrder';
import { isShippingQuoteUnavailable } from '@/lib/functions/isShippingQuoteUnavailable';

import type {
  OrderPayload,
  OrderPayloadItem,
} from '@/lib/types/orderPayload';

import styles from './cart.module.scss';

interface CartProps {
  orderData: OrderPayload;
  refreshCart: (showLoader: boolean) => void;
  setOrderData: React.Dispatch<React.SetStateAction<OrderPayload | null>>;
  onProceedToCheckout: () => void;
  onOpenAddressModal: (proceedAfterSuccess?: boolean) => void;
}

export default function Cart({
  orderData,
  refreshCart,
  setOrderData,
  onProceedToCheckout,
  onOpenAddressModal,
}: CartProps) {
  const cartItems: OrderPayloadItem[] = orderData.items ?? [];
  const hasShippingEstimate = orderHasShippingEstimate(orderData);
  const shippingUnavailable = isShippingQuoteUnavailable(orderData.pricing?.shipping);

  const handleProceedToCheckout = () => {
    if (shippingUnavailable) {
      return;
    }

    if (!hasShippingEstimate) {
      onOpenAddressModal(true);
      return;
    }

    onProceedToCheckout();
  };

  return (
    <div className={styles.cartWrapper}>
      <div className={styles.cart}>
        <CartItems items={cartItems} refreshCart={refreshCart} />
      </div>

      <div className={styles.cartCollaterals}>
        <section className={styles.wrapper}>
          <CartCheckoutCoupons setOrderData={setOrderData} orderData={orderData} />

          <div className={styles.table}>
            <CartCheckoutTotals
              pricing={orderData.pricing}
              state={orderData.states?.checkout}
              orderData={orderData}
              setIsModalOpen={() => onOpenAddressModal(false)}
              onCouponRemoved={() => refreshCart(false)}
            />
            <StylizedButton
              handleClick={handleProceedToCheckout}
              className={styles.checkoutButton}
              disabled={shippingUnavailable}
            >
              Proceed to Checkout
            </StylizedButton>
            {shippingUnavailable && (
              <p className={styles.shippingUnavailableMessage}>
                Shipping is unavailable for this order. An item may be too large to ship or cannot ship to your address.
              </p>
            )}
          </div>

          <CartCheckoutTemporaryAuthorization />
        </section>
      </div>
    </div>
  );
}
